import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';
import { IconButton } from './Button';

// HelpTip — a small "?" icon that tucks a page's explainer text behind a
// click-to-open panel (Jaylen 2026-07-29: "clean up help text behind a
// little help icon... do this for most pages"). First used on Capacity;
// sprinkle onto other text-heavy pages as we go.
//
// Usage:
//   <HelpTip title="How to read this page">
//     <p>...</p>
//     <p>...</p>
//   </HelpTip>
//
// Portal-rendered (portalRoot, so element-fullscreen still shows it),
// closes on outside click and ESC, z-50 (dropdown layer per CLAUDE.md).

export function HelpTip({ title = 'About this page', children, className = '' }) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const panelRef = useRef(null);
    const [pos, setPos] = useState(null);

    const toggle = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            const width = 360;
            // Anchor below the icon; clamp to the viewport's right edge.
            const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
            setPos({ top: r.bottom + 8, left, width });
        }
        setOpen(v => !v);
    };

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (panelRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                aria-label={title}
                aria-expanded={open}
                onClick={toggle}
                className={`w-7 h-7 rounded-full border-2 text-sm font-bold flex items-center justify-center transition-colors ${open ? 'bg-slate-700 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400'} ${className}`}
            >
                ?
            </button>
            {open && pos && createPortal(
                <div
                    ref={panelRef}
                    role="tooltip"
                    style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 50 }}
                    className="bg-white border border-slate-200 rounded-xl shadow-xl p-4 text-sm text-slate-600 space-y-2 max-h-[70vh] overflow-auto"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</span>
                        <IconButton size="sm" label="Close help" onClick={() => setOpen(false)}><span className="text-xs font-bold">✕</span></IconButton>
                    </div>
                    {children}
                </div>,
                portalRoot(),
            )}
        </>
    );
}

export default HelpTip;
